import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { EASE, GoldDiamond } from "../Reveal";
import { LuxButton } from "../LuxButton";
import { useUI } from "@/context/UIContext";
import { IMG } from "@/lib/images";

const MaskedLine = ({ children, delay, play, reduced }) => (
  <span className="-mb-[0.1em] block overflow-hidden pb-[0.14em]">
    <motion.span
      className="block"
      initial={reduced ? { opacity: 0 } : { y: "115%" }}
      animate={play ? (reduced ? { opacity: 1 } : { y: 0 }) : {}}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { loaderDone } = useUI();
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "7%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.025]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 42, damping: 22 });
  const smy = useSpring(my, { stiffness: 42, damping: 22 });

  const onMouseMove = (e) => {
    if (reduced) return;
    mx.set((e.clientX / window.innerWidth - 0.5) * 10);
    my.set((e.clientY / window.innerHeight - 0.5) * 6);
  };

  const play = loaderDone;

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex h-[90svh] min-h-[640px] items-center justify-center overflow-hidden md:h-[88vh] md:min-h-[720px]"
    >
      <motion.div
        className="absolute inset-0"
        style={reduced ? {} : { y: bgY, scale: bgScale }}
      >
        <motion.img
          src={IMG.hero}
          alt="Belle Époque grand hotel façade glowing at dusk"
          fetchpriority="high"
          initial={reduced ? { opacity: 0 } : { scale: 1.06, opacity: 0 }}
          animate={play ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 1.4, ease: EASE }}
          className="img-lux-grade h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0.35)] via-[rgba(0,0,0,0.25)] to-[rgba(0,0,0,0.78)]" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 52%, rgba(0,0,0,0.5) 100%)",
          }}
        />
      </motion.div>

      <motion.div
        className="relative z-10 mx-auto max-w-[880px] px-6 text-center"
        style={reduced ? {} : { x: smx, y: smy }}
      >
        <motion.div
          className="mb-9 flex justify-center"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={play ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
        >
          <GoldDiamond />
        </motion.div>

        <h1 className="font-serif text-[42px] font-medium leading-[1.07] tracking-[-0.01em] text-ivory sm:text-[58px] lg:text-[72px]">
          <MaskedLine delay={0.22} play={play} reduced={reduced}>
            Extraordinary access,
          </MaskedLine>
          <MaskedLine delay={0.36} play={play} reduced={reduced}>
            seamlessly <span className="gold-text italic">delivered.</span>
          </MaskedLine>
        </h1>

        <motion.p
          className="mx-auto mt-8 max-w-[620px] text-sm font-light leading-relaxed text-ivory-muted sm:text-base"
          initial={{ opacity: 0, y: 24 }}
          animate={play ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.62, ease: EASE }}
        >
          A private lifestyle and concierge service for those who expect more —
          connecting you to experiences and opportunities beyond reach. We unlock
          the exceptional.
        </motion.p>

        <motion.div
          className="mt-11 flex flex-col items-center justify-center gap-4 sm:flex-row"
          initial={{ opacity: 0, y: 24 }}
          animate={play ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.78, ease: EASE }}
        >
          <LuxButton variant="outline" to="/concierge" testId="hero-concierge-button" className="w-full sm:w-auto">
            Concierge
          </LuxButton>
          <LuxButton variant="gold" to="/membership" testId="hero-get-started-button" className="w-full sm:w-auto">
            Get Started
          </LuxButton>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-9 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
        initial={{ opacity: 0 }}
        animate={play ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 1.5 }}
      >
        <span className="text-[9px] font-light uppercase tracking-[0.42em] text-ivory-muted/70">
          Scroll
        </span>
        <motion.span
          className="block h-10 w-px origin-top bg-gradient-to-b from-champagne/70 to-transparent"
          animate={reduced ? {} : { scaleY: [1, 0.35, 1] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};
